import React, {useEffect, useMemo, useRef, useState} from 'react';
import ArrTreeView from '../../../../../common/arr-tree-view/ArrTreeView';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../../../shared/interfaces';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../../../redux/store';
import {Box} from '@mui/material';
import './SharedSavedSearchTree.scss';
import {getExpandedIdsForNode} from '../../../../../saved-search/SavedSearchUtils';
import {useGetSourceSelection} from '../../../../../../hooks/documentPrinting/useDocumentPrinting';
import {ISavedSearchTreeViewResponse} from '../../../../../../api/types/savedsearch.types';
import {t} from 'i18next';
import ArrLoadingSpinner from '../../../../../common/arr-loading-spinner/ArrLoadingSpinner';

interface ISavedSearchTree {
  getSelectedTreeItem: (item: string) => void;
  getSelectedTreeItemData?: (
    item: TreeViewBaseItem<ExtendedTreeItemProps> | undefined
  ) => void;
  searchKeyword?: string;
}

const SharedSavedSearchTree: React.FC<ISavedSearchTree> = ({
  getSelectedTreeItem,
  getSelectedTreeItemData,
  searchKeyword,
}) => {
  const createCustomIndex = useSelector(
    (state: RootState) => state?.indexManagement?.createCustomIndex
  );

  const storedSelectedItem = createCustomIndex?.savedSearchId?.toString() ?? '';

  const isReadOnlyView = useSelector(
    (state: RootState) => state?.indexManagement?.isReadOnlyView
  );
  const {data: treeData, isLoading} = useGetSourceSelection();

  const [selectedItem, setSelectedItem] = useState<string>(
    createCustomIndex?.savedSearchId?.toString() ?? ''
  );
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [treeKey, setTreeKey] = useState<number>(0);
  const expandTriggerRef = useRef(false);

  const transformTreeItems = (items: ISavedSearchTreeViewResponse[] = []) => {
    const filteredItems = items.filter(
      (it) => it.searchGroupTypeCode === 'PASHARED' && it?.customIndexID === 0
    );

    const nodeMap: Record<string, any> = {};

    filteredItems.forEach((it) => {
      nodeMap[it.id] = {
        ...it,
        label: it.text,
        skipParent: it.isGroup,
        disabled: isReadOnlyView,
      };
      delete nodeMap[it.id].children;
    });

    const roots: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

    filteredItems.forEach((it) => {
      const node = nodeMap[it.id];
      const parentId = it.parent;

      if (!parentId || !nodeMap[parentId]) {
        roots.push(node);
      } else {
        const parent = nodeMap[parentId];
        if (!parent.children) parent.children = [];
        parent.children.push(node);
      }
    });

    Object.values(nodeMap).forEach((n: any) => {
      n.hasChildren = Array.isArray(n.children) && n.children.length > 0;
    });

    return roots;
  };

  const transformedTree = useMemo(
    () => transformTreeItems(treeData?.sourceSelectionSearchList),
    [treeData?.sourceSelectionSearchList, isReadOnlyView]
  );

  useEffect(() => {
    if (!selectedItem) return;
    if (
      !treeData?.sourceSelectionSearchList ||
      !Array.isArray(treeData?.sourceSelectionSearchList)
    )
      return;

    // If Redux has stored value
    if (storedSelectedItem) {
      const storedNode = treeData?.sourceSelectionSearchList?.find(
        (node: any) => parseInt(node.id, 10).toString() === storedSelectedItem
      );

      if (storedNode) {
        setSelectedItem(storedNode.id);
        getSelectedTreeItem(storedNode.id);
        getSelectedTreeItemData?.(
          storedNode as unknown as TreeViewBaseItem<ExtendedTreeItemProps>
        );
        return;
      }
    }

    // fallback to first root node
    const firstNode = treeData?.sourceSelectionSearchList?.find(
      (node: any) => node.parent === '#' || node.parent === '-1'
    );

    if (firstNode) {
      setSelectedItem(firstNode.id);
      getSelectedTreeItem(firstNode.id);
      getSelectedTreeItemData?.(
        firstNode as unknown as TreeViewBaseItem<ExtendedTreeItemProps>
      );
    }
  }, [treeData?.sourceSelectionSearchList, searchKeyword]);

  useEffect(() => {
    if (storedSelectedItem && !expandTriggerRef.current) {
      const expanded = getExpandedIdsForNode(
        transformedTree,
        storedSelectedItem
      );
      setExpandedIds(expanded);
      setTreeKey((prev) => prev + 1);
    }
  }, [storedSelectedItem, JSON.stringify(transformedTree)]);

  const handleSelected = (item: string[] | null) => {
    if (
      Array.isArray(item) &&
      item.length > 0 &&
      treeData?.sourceSelectionSearchList
    ) {
      expandTriggerRef.current = true;
      setSelectedItem(item[0]);
      getSelectedTreeItem(item[0]);
      const foundItem = treeData?.sourceSelectionSearchList?.find(
        (data: any) => data.id == item[0]
      );
      getSelectedTreeItemData &&
        getSelectedTreeItemData?.(
          foundItem as unknown as TreeViewBaseItem<ExtendedTreeItemProps>
        );
    } else {
      setSelectedItem('');
      getSelectedTreeItem('');
    }
  };

  const treeSelectedItems = useMemo(() => [selectedItem], [selectedItem]);

  return (
    <Box
      className={`shared-search-tree-container ${isLoading && 'shared-search-loading-container'}`}
      data-testid="shared-saearch-tree-container"
    >
      {isLoading ? (
        <ArrLoadingSpinner />
      ) : (
        <ArrTreeView
          key={treeKey}
          setSelectedItem={handleSelected}
          showTooltip
          itemsList={transformedTree}
          selectedItem={selectedItem != null ? String(selectedItem) : ''}
          handleNoResultMessage={t('common.noResultFound')}
          allowGlobalSelect={false}
          treeSelectedItems={treeSelectedItems}
          multipleSelection={false}
          isShowCountLabel={true}
          showActions={false}
          expandSelect={false}
          searchKeyword={searchKeyword}
          defaultExpandedIds={expandedIds}
          setExpandedFolders={setExpandedIds}
          isShowCheckbox={true}
          checkboxGroupSelection={false}
          expandOnSearch={Boolean(searchKeyword)}
        />
      )}
    </Box>
  );
};

export default SharedSavedSearchTree;
